import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  DocumentLoadEvent,
  PageChangeEvent,
  Plugin,
  PluginFunctions,
  RenderPageProps,
} from '@react-pdf-viewer/core';
import { PdfViewer } from 'components/PdfViewer';
import {
  AgreementPageSize,
  getPageIndex,
  getPageNumber,
  getPageSizeInPoints,
} from 'agreementCoordinatesUtils';
import AgreementPageNavigation from './components/AgreementPageNavigation';

import './AgreementPdfViewer.styles.scss';

export type AgreementPageLayerProps = {
  pageNumber: number;
  scale: number;
  pageSize: AgreementPageSize;
};

type AgreementPdfViewerProps = {
  className?: string;
  fileUrl: string;
  PageLayer: React.ComponentType<AgreementPageLayerProps>;
  onDocumentLoad?: (pageCount: number) => void;
};

const AgreementPdfViewer: React.FC<AgreementPdfViewerProps> = (props) => {
  const { className, fileUrl, PageLayer, onDocumentLoad } = props;

  const [pageCount, setPageCount] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const pluginFunctions = useRef<PluginFunctions>();
  const onDocumentLoadRef = useRef(onDocumentLoad);

  onDocumentLoadRef.current = onDocumentLoad;

  const classes = classNames('agreement-pdf-viewer', className);

  const plugins = useMemo<Plugin[]>(
    () => [
      {
        install: (functions) => {
          pluginFunctions.current = functions;
        },
      },
    ],
    [],
  );

  const handleDocumentLoad = useCallback(
    ({ doc: { numPages } }: DocumentLoadEvent) => {
      setPageCount(numPages);
      setCurrentPageIndex(0);
      onDocumentLoadRef.current?.(numPages);
    },
    [],
  );

  const handlePageChange = useCallback(
    ({ currentPage }: PageChangeEvent) => setCurrentPageIndex(currentPage),
    [],
  );

  const renderPage = useCallback(
    ({
      canvasLayer,
      textLayer,
      annotationLayer,
      pageIndex,
      scale,
      width,
      height,
    }: RenderPageProps) => (
      <>
        {canvasLayer.children}
        {textLayer.children}
        {annotationLayer.children}
        <PageLayer
          pageNumber={getPageNumber(pageIndex)}
          scale={scale}
          pageSize={getPageSizeInPoints(width, height, scale)}
        />
      </>
    ),
    [PageLayer],
  );

  const viewer = useMemo(
    () => (
      <PdfViewer
        url={fileUrl}
        renderPage={renderPage}
        plugins={plugins}
        onDocumentLoad={handleDocumentLoad}
        onPageChange={handlePageChange}
      />
    ),
    [fileUrl, handleDocumentLoad, handlePageChange, plugins, renderPage],
  );

  return (
    <div className={classes}>
      <div className="agreement-pdf-viewer__document">{viewer}</div>

      {!!pageCount && (
        <AgreementPageNavigation
          currentPage={getPageNumber(currentPageIndex)}
          pageCount={pageCount}
          onPageChange={(page) =>
            pluginFunctions.current?.jumpToPage(getPageIndex(page))
          }
        />
      )}
    </div>
  );
};

export default AgreementPdfViewer;
